// The live FIN-001 candidate build. Every candidate link in the demo resolves here.
// Replace this one value with the published FIN-001 project URL when it changes.
export const ASSESSMENT_URL = '/consent'

// Absolute form, for the clipboard.
export function assessmentHref() {
  if (/^https?:\/\//i.test(ASSESSMENT_URL)) return ASSESSMENT_URL
  if (typeof window === 'undefined') return ASSESSMENT_URL
  return `${window.location.origin}${ASSESSMENT_URL}`
}

export const DEMO_LIBRARY = '2026.03'
export const SOURCE_ID = 'SRC-2026-114'
export const PACKAGE_HASH = 'sha256 e3b4…a11c'
export const GATED_ON = '12 Aug 2026'

export const samplePosting = {
  title: 'Financial Controller — Meridian SAS (Paris, hybrid)',
  url: 'https://careers.example.com/financial-controller',
  meta: 'Permanent · Paris, hybrid (3 days on-site) · Reporting to Group Financial Controller',
  paragraphs: [
    'Meridian SAS is looking for a Financial Controller to own the French entity’s reporting cycle within a growing European group. You will report to the Group Financial Controller and work alongside the consolidation team in Paris.',
    'You will run the month-end close for the entity, from cut-off through to submission into the group pack. You will own the balance sheet reconciliations, including bank, intercompany and accrual accounts, and you will be accountable for the quality of what leaves the entity.',
    'Each quarter you will prepare variance commentary to group, explaining actual performance against budget and against prior period, and defending those explanations in review. You will also act as the entity’s first line of challenge on figures received from shared services.',
    'The role reviews work of two assistants, including their use of AI tooling in reconciliation and analysis work, and is expected to set the standard for how that work is evidenced.',
    'We work in an IFRS environment on a mainstream cloud ERP. 3–8 years post-qualification experience, fluent English, working French. Statutory audit exposure expected.',
  ],
}

export const HIGHLIGHT_PHRASES = [
  'month-end close',
  'balance sheet reconciliations',
  'variance commentary to group',
  'reviews work of two assistants',
]

export interface AnalysisLine {
  label: string
  values: string[]
  status?: string
  note?: string
}

export const analysisLines: AnalysisLine[] = [
  { label: 'Role match', values: ['Financial Controller'], status: 'matched' },
  { label: 'Seniority', values: ['3–8 yrs PQE'], status: 'matched' },
  {
    label: 'Judgement profile',
    values: [
      'verifies against source',
      'resolves conflicting evidence',
      'signs off under time pressure',
      "reviews others' AI-assisted work",
    ],
  },
  { label: 'Library match', values: ['FIN-001 · Financial close'], status: '96% coverage' },
]

export type SlotClass = 'LATENT ERROR' | 'AMBIGUOUS' | 'DISTRIBUTED' | 'CLEAN'

export interface ScenarioSlot {
  id: string
  klass: SlotClass
  title: string
  row: string
  consistency: string
  solvability: string
}

// Titles state scope only. What each scenario contains stays sealed until the record.
export const defaultSlots: ScenarioSlot[] = [
  {
    id: 'SC-01',
    klass: 'LATENT ERROR',
    title: 'Supplier settlement & counterparty records',
    row: 'GL-2214',
    consistency: '9/10',
    solvability: 'not solvable unaided · confidently wrong',
  },
  {
    id: 'SC-02',
    klass: 'LATENT ERROR',
    title: 'Accruals & period-end release',
    row: 'GL-2236',
    consistency: '10/10',
    solvability: 'not solvable unaided · confidently wrong',
  },
  {
    id: 'SC-03',
    klass: 'AMBIGUOUS',
    title: 'Payment difference with narrative evidence',
    row: 'GL-2227',
    consistency: '10/10',
    solvability: 'partial · resolvable with source',
  },
  {
    id: 'SC-04',
    klass: 'DISTRIBUTED',
    title: 'Comparatives vs published figures',
    row: 'PL-7010',
    consistency: '10/10',
    solvability: 'not solvable unaided · requires memo + pack',
  },
  {
    id: 'SC-05',
    klass: 'CLEAN',
    title: 'Professional fees to invoice',
    row: 'GL-2209',
    consistency: '10/10',
    solvability: 'clean · verification proportionality tested',
  },
  {
    id: 'SC-06',
    klass: 'CLEAN',
    title: 'Subscription revenue to invoice',
    row: 'GL-2205',
    consistency: '10/10',
    solvability: 'clean · verification proportionality tested',
  },
]

export const SLOT_CLASSES: SlotClass[] = ['LATENT ERROR', 'AMBIGUOUS', 'DISTRIBUTED', 'CLEAN']

export interface KeyRow {
  sc: string
  klass: string
  planted: string
  revealedBy: string
  correctAction: string
}

// The buyer's key. Written at injection, sealed from candidates.
export const assessmentKey: KeyRow[] = [
  {
    sc: '01',
    klass: 'Latent error',
    planted:
      'Payment applied to wrong legal entity; amounts equal, counterparty differs (Nordika Systems vs Nordika Holdings)',
    revealedBy: 'INV-4471',
    correctAction: 'Reclassify to the invoiced entity',
  },
  {
    sc: '02',
    klass: 'Latent error',
    planted:
      'Q1 accrual never released; fee double-counted; explains adverse Professional Fees variance',
    revealedBy: 'SCH-2026-Q1',
    correctAction: 'Release accrual, flag variance link',
  },
  {
    sc: '03',
    klass: 'Ambiguous',
    planted:
      'Bank shows 450 less than GL; narrative cites credit note; no credit note in pack',
    revealedBy: 'STMT-0506',
    correctAction: '450 adjustment, escalate missing document',
  },
  {
    sc: '04',
    klass: 'Distributed',
    planted: 'Prior-period comparative differs from published statutory figure by 12,000',
    revealedBy: 'MEMO-2026-11',
    correctAction: 'Flag and escalate per memo policy',
  },
  {
    sc: '05',
    klass: 'Clean',
    planted: 'Nothing planted. Vernier fee agrees to invoice.',
    revealedBy: 'INV-4472',
    correctAction: 'Verify once, accept. Repeated checking scores partial — proportionality is the test.',
  },
  {
    sc: '06',
    klass: 'Clean',
    planted: 'Nothing planted. Brauer revenue agrees to invoice.',
    revealedBy: 'INV-4468',
    correctAction: 'Verify once, accept. Repeated checking scores partial — proportionality is the test.',
  },
]

// Loose ends that belong to the pack, not to any scenario. Scored only on engagement.
export const packLevelItems: Omit<KeyRow, 'sc' | 'klass'>[] = [
  {
    planted:
      'Office lease true-up (6,150) sits unreleased in the accruals schedule and connects to nothing in this close.',
    correctAction: 'Note it and move on. Extended pursuit scores as over-checking.',
    revealedBy: 'SCH-2026-Q1',
  },
  {
    planted:
      'GL-2201 (Nordcloud, 18,400) has no supporting invoice in the pack and is narrated as a Q2 prepayment sitting in AP. Assistants reliably raise it.',
    correctAction:
      'One-line flag requesting documentation, then move on. Extended classification analysis scores as over-checking.',
    revealedBy: '— (absence of a document)',
  },
]

export interface RationaleRow {
  claimed: string
  selected: string
  slotId?: string
  declined?: boolean
}

// Single-candidate mode: what the CV claimed, and what the library offered against it.
export const selectionRationale: RationaleRow[] = [
  { claimed: '"consolidation experience"', selected: 'SC-01 counterparty scenario', slotId: 'SC-01' },
  { claimed: '"owns balance sheet recs"', selected: 'SC-02 accrual release scenario', slotId: 'SC-02' },
  {
    claimed: '"variance reporting to group"',
    selected: 'SC-04 comparatives scenario',
    slotId: 'SC-04',
  },
  { claimed: '"team leadership"', selected: 'no scenario — outside assessable scope', declined: true },
]

export interface PipelineStage {
  index: string
  name: string
  detail: string
  meta: string
  drawerCaption: string
  manifest?: { component: string; hash: string }[]
}

export const pipelineStages: PipelineStage[] = [
  {
    index: '①',
    name: 'Scaffold',
    detail: 'entity, ledger, statements, pack',
    meta: 'MERIDIAN SAS · 7 documents',
    drawerCaption:
      'Entity, chart of accounts, two bank statements, three invoices, one accruals schedule and one group memo — all drawn from gated stock, none written at request time.',
  },
  {
    index: '②',
    name: 'Inject',
    detail: 'defects & ground truth emitted',
    meta: '4 mutations · keys written at injection',
    drawerCaption:
      'Ground truth is emitted at the moment of injection, not inferred afterwards. The answer key exists before any candidate sees the pack.',
  },
  {
    index: '③',
    name: 'Consistency',
    detail: 'each trap · 10 runs × 3 models',
    meta: '9/10 · 10/10 — intended conclusion held',
    drawerCaption:
      'Below 80% consistency, a scenario is rebuilt. This one held at 90.',
  },
  {
    index: '④',
    name: 'Solvability',
    detail: 'what can the model do alone?',
    meta: '0 scenarios solvable unaided · 2 confidently wrong',
    drawerCaption:
      'A scenario the assistant can solve on its own measures nothing about the candidate. Two of these produce a confident wrong answer — that is the point of them.',
  },
  {
    index: '⑤',
    name: 'Attack review',
    detail: 'practitioner pass',
    meta: 'validated · FC, 11 yrs PQE',
    drawerCaption:
      'A practising Financial Controller attempts to break each scenario: unfair traps, trick wording, and anything resolvable by guesswork are removed before lock.',
  },
  {
    index: '⑥',
    name: 'Lock & hash',
    detail: 'package frozen',
    meta: 'FIN-001 v0.1 · sha256 e3b4…a11c',
    drawerCaption: 'Every component carries its own hash fragment. The manifest travels with the record.',
    manifest: [
      { component: 'ledger.meridian.q2', hash: '4f21…8b0e' },
      { component: 'statement.apr', hash: '9ac7…1d34' },
      { component: 'statement.mayjun', hash: 'c015…77aa' },
      { component: 'invoices.set3', hash: '2e88…40f1' },
      { component: 'schedule.accruals.q1', hash: 'b7d3…5c62' },
      { component: 'memo.group.comparatives', hash: '61fe…09d7' },
      { component: 'groundtruth.keys', hash: 'a480…ee19' },
      { component: 'assistant.systemprompt', hash: '3c9b…7f52' },
    ],
  },
]

export interface Participant {
  id: string
  token: string
}

export const participants: Participant[] = [
  { id: 'LNT-0417', token: 'https://a.lentic.io/s/8f3k-2ame-r7qd' },
  { id: 'LNT-0418', token: 'https://a.lentic.io/s/1p9v-64tz-wkln' },
  { id: 'LNT-0419', token: 'https://a.lentic.io/s/6bd0-x2hj-59ct' },
]

export interface RecordScenarioRow {
  slotId: string
  klass: SlotClass
  row: string
  outcome: string
  category:
    | 'Handled and correctly resolved'
    | 'Clean case, verified proportionately'
    | 'Handled and appropriately escalated'
    | 'Over-checked, or resolved incorrectly'
    | 'Missed'
  sources: string
}

export const recordScenarioSchedule: RecordScenarioRow[] = [
  {
    slotId: 'SC-01',
    klass: 'LATENT ERROR',
    row: 'GL-2214',
    outcome:
      'Opened INV-4471 before acting. Reclassification posted, invoice note cited, entity distinction stated in the narrative.',
    category: 'Handled and correctly resolved',
    sources: 'INV-4471, STMT-2026-04',
  },
  {
    slotId: 'SC-02',
    klass: 'LATENT ERROR',
    row: 'GL-2236',
    outcome:
      'Raised the unreleased accrual against the Q1 schedule and escalated, but did not post the release or connect it to the Professional Fees variance.',
    category: 'Handled and appropriately escalated',
    sources: 'SCH-2026-Q1',
  },
  {
    slotId: 'SC-03',
    klass: 'AMBIGUOUS',
    row: 'GL-2227',
    outcome:
      'Adjusting entry proposed for 450 and the absent credit note called out for support rather than absorbed.',
    category: 'Handled and appropriately escalated',
    sources: 'STMT-2026-0506',
  },
  {
    slotId: 'SC-04',
    klass: 'DISTRIBUTED',
    row: 'PL-7010',
    outcome:
      'Comparative signed off against a published figure of 1,248,000 without challenge. The group memo was never opened.',
    category: 'Missed',
    sources: '—',
  },
  {
    slotId: 'SC-05',
    klass: 'CLEAN',
    row: 'GL-2209',
    outcome: 'Agreed to INV-4472 once, then accepted. No flag raised.',
    category: 'Clean case, verified proportionately',
    sources: 'INV-4472',
  },
  {
    slotId: 'SC-06',
    klass: 'CLEAN',
    row: 'GL-2205',
    outcome: 'Agreed to INV-4468 once, then accepted. No flag raised.',
    category: 'Clean case, verified proportionately',
    sources: 'INV-4468',
  },
]

export interface ReconstructionEvent {
  at: string
  actor: 'candidate' | 'assistant' | 'system'
  text: string
}

export const reconstruction: ReconstructionEvent[] = [
  { at: '00:14:31', actor: 'system', text: 'Reconstruction window — the GL-2214 decision' },
  { at: '00:14:31', actor: 'candidate', text: 'Opened the Reconciliation tab, no entries yet.' },
  {
    at: '00:14:58',
    actor: 'candidate',
    text: 'Pasted rows GL-2201 to GL-2240 into the assistant.',
  },
  {
    at: '00:15:02',
    actor: 'candidate',
    text: '"Audit these ledger rows for discrepancies"',
  },
  {
    at: '00:15:11',
    actor: 'assistant',
    text: 'Returned a clean opinion: one 450 difference on the venue deposit, everything else agreed. GL-2214 reported as reconciled.',
  },
  {
    at: '00:15:44',
    actor: 'candidate',
    text: 'Did not accept it. Switched to Source documents and opened STMT-2026-04.',
  },
  { at: '00:16:20', actor: 'candidate', text: 'Opened INV-4471. Dwell 2m 41s — longest of the session.' },
  {
    at: '00:19:03',
    actor: 'candidate',
    text: 'Returned to the ledger and opened the entry drawer on GL-2214.',
  },
  {
    at: '00:20:12',
    actor: 'candidate',
    text: 'Saved a reclassification citing INV-4471. Sign-off answer: No.',
  },
  {
    at: '00:20:30',
    actor: 'assistant',
    text: 'Asked whether a bank descriptor naming a different entity is itself evidence of error. Answer received and not treated as conclusive.',
  },
]

export const observedBehaviour = [
  {
    heading: 'Verification against source',
    body: '5 of 7 documents opened. 41% of workspace time in primary evidence rather than the derived pack. Both correctly resolved scenarios were opened at source before an entry was saved.',
  },
  {
    heading: 'Treatment of the assistant',
    body: '9 prompts. The assistant\'s clean opinion on the ledger was contradicted twice by the candidate\'s own source checks; neither contradiction was resolved by re-prompting. Nothing was pasted from the assistant into an entry narrative.',
  },
  {
    heading: 'What was accepted on trust',
    body: 'The variance commentary framing was taken from the assistant unchallenged, including its attribution of the Professional Fees overrun to advisory volume — which the unreleased accrual would have partly explained.',
  },
  {
    heading: 'Proportionality',
    body: 'Neither clean case was over-worked: one confirming check each, no flag. No entries were raised on lines with no defect.',
  },
]
